import { supabase } from '../lib/supabase';

export const getSubscriptionPlans = async () => {
  try {
    if (!supabase) return getDefaultPlans();
    const { data, error } = await supabase
      ?.from('subscription_plans')
      ?.select('*')
      ?.eq('is_active', true)
      ?.order('amount', { ascending: true });
    if (error) {
      console.warn('Error fetching subscription plans:', error?.message);
      return getDefaultPlans();
    }
    if (!data || data?.length === 0) return getDefaultPlans();
    // Normalize amounts: if any plan has amount > 1000, it's stored in cents
    const normalized = data?.map(plan => ({
      ...plan,
      amount: plan?.amount > 1000 ? Math.round(plan?.amount / 100) : plan?.amount
    }));
    return normalized;
  } catch (err) {
    console.error('Error in getSubscriptionPlans:', err);
    return getDefaultPlans();
  }
};

export const getUserSubscription = async (userId) => {
  if (!userId) return null;
  try {
    if (!supabase) return null;
    const { data, error } = await supabase
      ?.from('subscriptions')
      ?.select('*, subscription_plans(*)')
      ?.eq('user_id', userId)
      ?.eq('status', 'active')
      ?.maybeSingle();
    if (error) {
      console.warn('Error fetching user subscription:', error?.message);
      return null;
    }
    return data || null;
  } catch (err) {
    console.error('Error in getUserSubscription:', err);
    return null;
  }
};

export const getUserUsage = async (userId) => {
  if (!userId) return [];
  try {
    if (!supabase) return [];
    const { data, error } = await supabase
      ?.from('usage_tracking')
      ?.select('*')
      ?.eq('user_id', userId);
    if (error) {
      console.warn('Error fetching usage data:', error?.message);
      const [leadsResult, campaignsResult] = await Promise.allSettled([
        supabase?.from('funnel_leads')?.select('*', { count: 'exact', head: true })?.eq('user_id', userId),
        supabase?.from('communication_campaigns')?.select('*', { count: 'exact', head: true })?.eq('user_id', userId)
      ]);
      return [
        { resource_type: 'leads', usage_count: leadsResult?.value?.count || 0, usage_limit: 1000 },
        { resource_type: 'campaigns', usage_count: campaignsResult?.value?.count || 0, usage_limit: 50 }
      ];
    }
    return data || [];
  } catch (err) {
    console.error('Error in getUserUsage:', err);
    return [];
  }
};

export const cancelSubscription = async (subscriptionId) => {
  if (!subscriptionId) return { success: false, error: 'Subscription ID required' };
  try {
    if (!supabase) return { success: false, error: 'Database not available' };
    const { error } = await supabase
      ?.from('subscriptions')
      ?.update({ status: 'cancelled', updated_at: new Date()?.toISOString() })
      ?.eq('id', subscriptionId);
    if (error) throw error;
    return { success: true };
  } catch (err) {
    console.error('Error cancelling subscription:', err);
    return { success: false, error: err?.message };
  }
};

export const getFeatureAccess = async (userId) => {
  if (!userId) return null;
  try {
    if (!supabase) return getDefaultFeatureAccess();
    const { data, error } = await supabase
      ?.from('subscriptions')
      ?.select('*, subscription_plans(features)')
      ?.eq('user_id', userId)
      ?.eq('status', 'active')
      ?.maybeSingle();
    if (error) {
      return getDefaultFeatureAccess();
    }
    const features = data?.subscription_plans?.features || {};
    return {
      aiFeatures: features?.ai_features !== false,
      advancedAnalytics: features?.advanced_analytics !== false,
      apiAccess: features?.api_access !== false,
      teamCollaboration: features?.team_collaboration !== false,
      customIntegrations: features?.custom_integrations !== false,
      prioritySupport: features?.priority_support !== false
    };
  } catch (err) {
    console.error('Error in getFeatureAccess:', err);
    return getDefaultFeatureAccess();
  }
};

export const upgradePlan = async (userId, planId, paymentMethodId) => {
  if (!userId || !planId) return { success: false, error: 'User ID and Plan ID required' };
  try {
    if (!supabase) return { success: false, error: 'Database not available' };
    await supabase?.from('subscriptions')?.update({ status: 'cancelled' })?.eq('user_id', userId)?.eq('status', 'active');
    const { data, error } = await supabase
      ?.from('subscriptions')
      ?.insert([{
        user_id: userId,
        plan_id: planId,
        status: 'active',
        current_period_start: new Date()?.toISOString(),
        current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString(),
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      }])
      ?.select()
      ?.single();
    if (error) throw error;
    return { success: true, data };
  } catch (err) {
    console.error('Error upgrading plan:', err);
    return { success: false, error: err?.message };
  }
};

// Default plans with correct dollar amounts: Free $0, Basic $163, Professional $384, Premium $520, Enterprise $649
const getDefaultPlans = () => [
  {
    id: 'free',
    name: 'Free',
    amount: 0,
    billing_interval: 'monthly',
    is_active: true,
    description: 'Perfect for getting started',
    features: { leads: 100, campaigns: 5, users: 1, emails: 500, calls: 50, ai_features: false, advanced_analytics: false, priority_support: false, custom_integrations: 0 }
  },
  {
    id: 'basic',
    name: 'Basic',
    amount: 163,
    billing_interval: 'monthly',
    is_active: true,
    description: 'Great for small businesses',
    features: { leads: 1000, campaigns: 20, users: 3, emails: 5000, calls: 200, ai_features: true, advanced_analytics: false, priority_support: false, custom_integrations: 0 }
  },
  {
    id: 'professional',
    name: 'Professional',
    amount: 384,
    billing_interval: 'monthly',
    is_active: true,
    description: 'Most popular for growing teams',
    features: { leads: 5000, campaigns: 100, users: 10, emails: 25000, calls: 1000, ai_features: true, advanced_analytics: true, priority_support: true, custom_integrations: 5 }
  },
  {
    id: 'premium',
    name: 'Premium',
    amount: 520,
    billing_interval: 'monthly',
    is_active: true,
    description: 'Enterprise-grade solution',
    features: { leads: 15000, campaigns: 500, users: 25, emails: 60000, calls: 5000, ai_features: true, advanced_analytics: true, priority_support: true, custom_integrations: 15, api_access: true, team_collaboration: true }
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    amount: 649,
    billing_interval: 'monthly',
    is_active: true,
    description: 'For large organizations',
    features: { leads: 25000, campaigns: -1, users: -1, emails: 100000, calls: -1, ai_features: true, advanced_analytics: true, priority_support: true, custom_integrations: -1, api_access: true, team_collaboration: true, dedicated_manager: true }
  }
];

const getDefaultFeatureAccess = () => ({
  aiFeatures: false,
  advancedAnalytics: false,
  apiAccess: false,
  teamCollaboration: false,
  customIntegrations: false,
  prioritySupport: false
});

export const subscriptionService = {
  getSubscriptionPlans,
  getUserSubscription,
  getUserUsage,
  cancelSubscription,
  getFeatureAccess,
  upgradePlan
};

export default subscriptionService;

